import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Package, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  LoadingState,
} from '@/components/common';
import { locationsApi, Location } from '@/api/services';
import { getErrorMessage } from '@/api/client';

const locationTypeColors: Record<string, string> = {
  HUB: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  SITE: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  DEPOT: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  SUPPLIER: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
};

const statusMap: Record<string, 'optimal' | 'low' | 'stockout' | 'overstock'> = {
  Optimal: 'optimal',
  Low: 'low',
  Stockout: 'stockout',
  Overstock: 'overstock',
};

interface LocationInventory {
  id: string;
  itemId: string;
  item: { id: string; sku: string; name: string; unit: string; category: string; unitCost?: number };
  quantity: number;
  reservedQty: number;
  availableQty: number;
  status?: string;
}

export default function LocationDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [location, setLocation] = useState<Location | null>(null);
  const [inventory, setInventory] = useState<LocationInventory[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [locationData, inventoryData] = await Promise.all([
        locationsApi.getById(id),
        locationsApi.getInventory(id),
      ]);
      setLocation(locationData);
      setInventory(inventoryData.data || inventoryData || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/locations');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  if (loading) {
    return <LoadingState />;
  }

  if (!location) {
    return null;
  }

  const totalItems = inventory.length;
  const totalQuantity = inventory.reduce((sum, inv) => sum + inv.quantity, 0);
  const totalValue = inventory.reduce((sum, inv) => sum + (inv.quantity * (inv.item?.unitCost || 0)), 0);
  const lowStockCount = inventory.filter(inv => inv.status === 'Low' || inv.status === 'Stockout').length;

  const inventoryColumns: Column<LocationInventory>[] = [
    {
      key: 'item',
      header: 'Item',
      render: (inv) => (
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
            <Package className="h-4 w-4 text-primary" />
          </div>
          <div>
            <div className="font-medium">{inv.item?.name || '-'}</div>
            <div className="text-sm text-muted-foreground">{inv.item?.sku}</div>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      render: (inv) => (
        <Badge variant="secondary">{inv.item?.category || '-'}</Badge>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      render: (inv) => (
        <span className="font-medium">{inv.quantity} {inv.item?.unit}</span>
      ),
    },
    {
      key: 'reserved',
      header: 'Reserved',
      render: (inv) => (
        <span className="text-muted-foreground">{inv.reservedQty} {inv.item?.unit}</span>
      ),
    },
    {
      key: 'available',
      header: 'Available',
      render: (inv) => (
        <span className="font-medium text-emerald-600">{inv.availableQty} {inv.item?.unit}</span>
      ),
    },
    {
      key: 'value',
      header: 'Value',
      render: (inv) => (
        <span className="text-muted-foreground">
          ${((inv.quantity * (inv.item?.unitCost || 0))).toLocaleString()}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (inv) => (
        <StatusBadge status={statusMap[inv.status || 'Optimal'] || 'optimal'} />
      ),
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/locations')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <PageHeader
          title={location.name}
          description={`Code: ${location.code}`}
          breadcrumbs={[
            { label: 'Locations', href: '/locations' },
            { label: location.name },
          ]}
          actions={
            <Button variant="outline">
              <Edit className="mr-2 h-4 w-4" />
              Edit Location
            </Button>
          }
        />
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          label="Total Items"
          value={totalItems}
          icon={<Package className="h-5 w-5" />}
        />
        <MetricCard
          label="Total Quantity"
          value={totalQuantity.toLocaleString()}
        />
        <MetricCard
          label="Inventory Value"
          value={`$${totalValue.toLocaleString()}`}
        />
        <MetricCard
          label="Low Stock"
          value={lowStockCount}
          trend={lowStockCount > 0 ? { direction: 'down', value: '', text: 'needs attention' } : undefined}
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        {/* Location Details */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              Location Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Status</span>
              <StatusBadge status={location.isActive ? 'active' : 'inactive'} />
            </div>
            <Separator />
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Type</span>
              <Badge className={locationTypeColors[location.type] || ''}>
                {location.type}
              </Badge>
            </div>
            <Separator />

            {/* Address */}
            {location.address && (
              <div className="space-y-2">
                <span className="text-sm font-medium">Address</span>
                <div className="text-sm text-muted-foreground">
                  {location.address.street && <p>{location.address.street}</p>}
                  <p>
                    {[location.address.city, location.address.state, location.address.postalCode]
                      .filter(Boolean)
                      .join(', ')}
                  </p>
                  {location.address.country && <p>{location.address.country}</p>}
                </div>
              </div>
            )}

            {/* Contact */}
            {location.contact && (
              <>
                <Separator />
                <div className="space-y-2">
                  <span className="text-sm font-medium">Contact</span>
                  {location.contact.name && (
                    <p className="text-sm text-muted-foreground">{location.contact.name}</p>
                  )}
                  {location.contact.email && (
                    <p className="text-sm text-muted-foreground">{location.contact.email}</p>
                  )}
                  {location.contact.phone && (
                    <p className="text-sm text-muted-foreground">{location.contact.phone}</p>
                  )}
                </div>
              </>
            )}

            <Separator />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Shipments (Origin)</span>
              <span>{location._count?.shipmentOrigins || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Shipments (Dest)</span>
              <span>{location._count?.shipmentDestinations || 0}</span>
            </div>
          </CardContent>
        </Card>

        {/* Coordinates Map Placeholder */}
        {location.coordinates && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Map</CardTitle>
              <CardDescription>
                Coordinates: {location.coordinates.lat}, {location.coordinates.lng}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex h-48 items-center justify-center rounded-lg bg-muted">
                <div className="text-center text-muted-foreground">
                  <MapPin className="h-8 w-8 mx-auto mb-2" />
                  <p>Map view coming soon</p>
                  <p className="text-sm">
                    {location.coordinates.lat}, {location.coordinates.lng}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {!location.coordinates && (
          <Card className="lg:col-span-2">
            <CardHeader>
              <CardTitle>Map</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex h-48 items-center justify-center rounded-lg bg-muted">
                <div className="text-center text-muted-foreground">
                  <MapPin className="h-8 w-8 mx-auto mb-2" />
                  <p>No coordinates set</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Inventory */}
      <Card>
        <CardHeader>
          <CardTitle>Inventory at this Location</CardTitle>
          <CardDescription>{inventory.length} item(s) stocked at this location</CardDescription>
        </CardHeader>
        <CardContent>
          <DataTable
            data={inventory}
            columns={inventoryColumns}
            onRowClick={(inv) => navigate(`/inventory/${inv.itemId}`)}
            emptyTitle="No inventory"
            emptyDescription="No items have been stocked at this location."
          />
        </CardContent>
      </Card>
    </div>
  );
}
